import {
  UserAssignBreakoutParticipantRequest,
  UserBroadcastToBreakoutRoomsRequest,
  UserCloseBreakoutRoomsRequest,
  UserCreateBreakoutRoomsRequest,
  UserCreateBreakoutRoomsResponse,
  UserGetBreakoutPresenceRequest,
  UserGetBreakoutPresenceResponse,
  UserGetBreakoutTokenRequest,
  UserGetBreakoutTokenResponse,
  UserGetViewerBreakoutStateRequest,
  UserGetViewerBreakoutStateResponse,
  UserStartStreamDestinationRequest,
  UserStopStreamDestinationRequest,
} from 'cb-model';
import { getConfigOrThrow } from 'gossamer-front-end';

export interface LivestreamApiResponse {
  status: number;
  error?: string;
  message?: string;
  result?: unknown;
  [key: string]: unknown;
}

const request = async <T extends Record<string, unknown>>(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
): Promise<{ data: T; httpStatus: number }> => {
  const baseUrl = getConfigOrThrow('gossamer-be-origin');
  const response = await fetch(`${baseUrl}/gossamer/main${path}`, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'x-fe-path': window.location.pathname,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await response.json().catch(() => ({}))) as T;
  return { data, httpStatus: response.status };
};

export const getAccessToken = async (room: string, role: 'host' | 'viewer', isLiveStreamPreview = false) => {
  const response = await request<{ token?: string; isHost?: boolean; error?: string; message?: string }>(
    'POST',
    '/livestream/getToken',
    { room, role, isLiveStreamPreview },
  );
  return [response.data, response.httpStatus] as const;
};

export const joinLivestream = async (livestreamSessionId: string) => {
  const response = await request<{ error?: string; message?: string }>('POST', '/livestream/join', {
    livestreamSessionId,
  });
  return [response.data, response.httpStatus] as const;
};

export const getLivestreamSession = async (livestreamSessionId: string) => {
  const response = await request<{ status?: string; error?: string; message?: string }>(
    'GET',
    `/livestream/${livestreamSessionId}`,
  );
  return [response.data, response.httpStatus] as const;
};

export const endRoom = async (room: string) => {
  const response = await request<{ success?: boolean; error?: string; message?: string }>(
    'POST',
    '/livestream/endRoom',
    { room },
  );
  return [response.data, response.httpStatus] as const;
};

export const sendLivestreamStartEmails = async (roomId: string) => {
  const response = await request<{ error?: string; message?: string }>('POST', '/livestream/sendStartEmails', {
    roomId,
  });
  return [response.data, response.httpStatus] as const;
};

export const startEgress = async (livestreamSessionId: string) => {
  const response = await request<{ status?: string; error?: string; message?: string }>(
    'POST',
    '/livestream/startEgress',
    { livestreamSessionId },
  );
  return [response.data, response.httpStatus] as const;
};

const transactionResponse = async <T extends LivestreamApiResponse>(
  promise: Promise<{ data: T; httpStatus: number }>,
): Promise<T> => {
  const { data, httpStatus } = await promise;
  return { status: httpStatus, ...data };
};

export const registerEventAttendanceLivestream = (eventId: number) =>
  transactionResponse(request<LivestreamApiResponse>('POST', '/user-record-attendance-livestream', { eventId }));

export const startStreamDestination = (payload: UserStartStreamDestinationRequest) =>
  transactionResponse(request<LivestreamApiResponse>('POST', '/livestream-destination-start', payload));

export const stopStreamDestination = (payload: UserStopStreamDestinationRequest) =>
  transactionResponse(request<LivestreamApiResponse>('POST', '/livestream-destination-stop', payload));

export const createBreakoutRooms = (payload: UserCreateBreakoutRoomsRequest) =>
  transactionResponse(
    request<LivestreamApiResponse & { result?: UserCreateBreakoutRoomsResponse }>('POST', '/breakout-create', payload),
  );

export const assignBreakoutParticipant = (payload: UserAssignBreakoutParticipantRequest) =>
  transactionResponse(request<LivestreamApiResponse>('POST', '/breakout-assign', payload));

export const closeBreakoutRooms = (payload: UserCloseBreakoutRoomsRequest) =>
  transactionResponse(request<LivestreamApiResponse>('POST', '/breakout-close', payload));

export const broadcastToBreakoutRooms = (payload: UserBroadcastToBreakoutRoomsRequest) =>
  transactionResponse(request<LivestreamApiResponse>('POST', '/breakout-broadcast', payload));

export const getBreakoutToken = (payload: UserGetBreakoutTokenRequest) =>
  transactionResponse(
    request<LivestreamApiResponse & { result?: UserGetBreakoutTokenResponse }>('POST', '/breakout-get-token', payload),
  );

export const getBreakoutPresence = (payload: UserGetBreakoutPresenceRequest) =>
  transactionResponse(
    request<LivestreamApiResponse & { result?: UserGetBreakoutPresenceResponse }>(
      'POST',
      '/breakout-presence',
      payload,
    ),
  );

export const getViewerBreakoutState = (payload: UserGetViewerBreakoutStateRequest) =>
  transactionResponse(
    request<LivestreamApiResponse & { result?: UserGetViewerBreakoutStateResponse }>(
      'POST',
      '/breakout-viewer-state',
      payload,
    ),
  );
